import { useDroppable } from '@dnd-kit/core';
import {
  bendsARule,
  formatDuration,
  isCancelled,
  isExternal,
  isUnpublished,
  minutesToHm,
  publicationState,
  publishable,
} from '@reditelji/shared';
import { plPred, plS } from '../lib/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useShiftMutations } from '../api/hooks';
import { useSession } from '../api/session';
import type { PoolVolunteer, Shift, TimelinePayload, TimelinePost } from '../api/types';
import { checkinsByShift, ciState, lateLevel, LATE_BLOCK, liveViewsByShift, type ShiftCheckins } from '../live/derive';
import type { DayPhase } from '../lib/ui';
import { useUi, type FilterChip } from '../state/ui';
import {
  blockMatches,
  blockStatus,
  briefingStale,
  fillTargets,
  gapMatches,
  gapMismatch,
  mismatchedShifts,
  planningOn,
  postRunsOnDay,
  resizeBounds,
  postMatches,
  proposalReason,
  holderLabel,
  range,
  rowCoverage,
  shiftConfirmation,
  type BandValue,
  type BlockStatus,
  type LensFit,
  type ResizeBounds,
} from './derive';
import { useAxis } from './axisContext';
import { useDayKey } from './dayContext';
import {
  centreScrollLeft,
  hourTicks,
  minToPct,
  minTrackWidth,
  snap15,
  spanPct,
  xToMin,
  type Axis,
} from './geometry';
import { useDropPreview } from './dropPreview';
import { gapKey } from './planReview';
import { VolunteerHoverCard } from './VolunteerHoverCard';
import type { AutofillProposal, AutofillUnfilled, LiveShiftView } from '@reditelji/shared';

/**
 * The person the board is looking through (SPEC §1.1B), as TimelinePage
 * decided it applies: the hours their sheet closed or left blank, and where
 * they fit, per post. Drawn on the post rows only.
 */
export interface BoardLens {
  volunteerId: string;
  name: string;
  bands: { fromMin: number; toMin: number; value: BandValue }[];
  fitsByPost: Map<string, LensFit[]>;
  canAct: boolean;
  onAssign: (fit: LensFit) => void;
}

const TYPE_ORDER: { key: string; label: string }[] = [
  { key: 'trasa', label: 'trasa' },
  { key: 'štartno-ciljni', label: 'štartno-ciljni' },
  { key: 'parkirišče', label: 'parkirišče' },
];

/** Stable identity so PostRow does not re-render on every parent pass. */
const EMPTY_FILTERS: ReadonlySet<FilterChip> = new Set();

/**
 * Below this the block has no room for two handles and a click target — at the
 * axis density floor a quarter-hour block is about eleven pixels wide.
 */
const MIN_HANDLE_PCT = 1.6;

const RAIL_CLASS: Record<string, string> = {
  trasa: 'tl-rail--trasa',
  'parkirišče': 'tl-rail--parkirisce',
  'štartno-ciljni': 'tl-rail--startno',
};

export function Board({
  data,
  proposals,
  unfilled,
  phase,
  live,
  now,
  onAcceptProposal,
  onRejectProposal,
  lens,
}: {
  data: TimelinePayload;
  proposals: AutofillProposal[];
  unfilled: AutofillUnfilled[];
  phase: DayPhase;
  live: boolean;
  now: number;
  onAcceptProposal: (p: AutofillProposal) => void;
  onRejectProposal: (p: AutofillProposal) => void;
  lens: BoardLens | null;
}) {
  const {
    boardView,
    sectorFocus,
    filters,
    collapsedGroups,
    toggleGroup,
    setSectorFocus,
    nowSignal,
    timelineReveal,
    clearTimelineReveal,
    planFocusOnly,
    focusedProposal,
  } = useUi();
  const axis = useAxis();
  const boardRef = useRef<HTMLDivElement>(null);
  const volunteersById = useMemo(
    () => new Map(data.volunteers.map((v) => [v.id, v])),
    [data.volunteers],
  );
  const checkinMap = useMemo(() => checkinsByShift(data.checkins), [data.checkins]);
  // Back-to-back shifts read as one stretch, as on the map (`liveShiftViews`).
  const liveViews = useMemo(
    () => (live ? liveViewsByShift(data.posts, checkinMap, now) : new Map<string, LiveShiftView>()),
    [live, data.posts, checkinMap, now],
  );

  // "zdaj HH:MM" / entering live mode centres the track on the current time;
  // a moment asked for from elsewhere (the Pregled heatmap) takes precedence.
  const prevSignal = useRef<number | null>(null);
  useEffect(() => {
    const el = boardRef.current;
    if (!el) return;
    const first = prevSignal.current === null;
    const signalled = prevSignal.current !== null && nowSignal !== prevSignal.current;
    prevSignal.current = nowSignal;
    const centre = (min: number) => {
      const track = el.querySelector('.tl-track');
      if (!track) return;
      el.scrollLeft = centreScrollLeft({
        axis,
        min,
        trackWidth: track.getBoundingClientRect().width,
        clientWidth: el.clientWidth,
        scrollWidth: el.scrollWidth,
      });
    };
    if (timelineReveal) {
      // Spent in one go, on this day or not, so it never scrolls a later board.
      if (timelineReveal.dayId === data.day.id) {
        centre(timelineReveal.min);
        if (timelineReveal.postId) {
          el.querySelector(`[data-post-row="${CSS.escape(timelineReveal.postId)}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      }
      clearTimelineReveal();
      return;
    }
    if ((first && live) || signalled) centre(now);
  }, [nowSignal, live, now, timelineReveal, data.day.id]);
  // Keep the proposal under the keyboard walk on screen.
  useEffect(() => {
    if (!focusedProposal) return;
    const el = boardRef.current?.querySelector(`[data-proposal="${CSS.escape(focusedProposal)}"]`);
    el?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [focusedProposal]);

  const sectorsById = useMemo(() => new Map(data.sectors.map((s) => [s.id, s])), [data.sectors]);
  // Uncovered stretches per post, each once — what a group's "opozoril" counts.
  const targetsByPost = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of fillTargets(data)) m.set(t.postId, (m.get(t.postId) ?? 0) + 1);
    return m;
  }, [data]);
  const conflictShiftIds = useMemo(
    () => new Set(data.conflicts.flatMap((c) => c.shiftIds)),
    [data.conflicts],
  );

  /**
   * Shifts whose person marked a slot they run into "ni na voljo".
   *
   * Planning only. During the event what matters is who is standing where and
   * who is late; what somebody wrote on a signup sheet a month earlier changes
   * nothing on the day, and the block's mark slot is spoken for by ! and ●.
   */
  const mismatchShiftIds = useMemo(() => {
    if (!planningOn(live, phase)) return new Set<string>();
    return new Set(mismatchedShifts(data).map((m) => m.shift.id));
  }, [data, live, phase]);

  const touchedPostIds = useMemo(
    () => new Set([...proposals.map((p) => p.gap.postId), ...unfilled.map((g) => g.postId)]),
    [proposals, unfilled],
  );

  // The chips only exist in planning mode (Toolbar), but nothing clears them on
  // the way out — so without this a chip left on keeps filtering the board with
  // no control on screen to switch it off.
  const filtering = filters.size > 0 && planningOn(live, phase) && boardView === 'mesta';
  // The organisation reads the board; its edges do not move (SPEC §1.1L).
  const canPlan = useSession().can('plan:write');
  // Edges are draggable while planning only: a finished day is archived, and in
  // live mode the times are what people are standing to.
  const canResize = planningOn(live, phase) && boardView === 'mesta' && canPlan;

  const visiblePosts = useMemo(() => {
    let posts = data.posts.filter(postRunsOnDay);
    // Plan mode: the rows the plan has something to say about, and no others.
    if (planFocusOnly && touchedPostIds.size > 0) {
      posts = posts.filter((p) => touchedPostIds.has(p.id));
    }
    if (sectorFocus) {
      posts = posts.filter((p) => sectorsById.get(p.sectorId)?.name === sectorFocus);
    }
    if (filtering) {
      posts = posts.filter((p) =>
        postMatches(filters, p, (id) => volunteersById.get(id), touchedPostIds.has(p.id)),
      );
    }
    return posts;
  }, [
    data.posts,
    planFocusOnly,
    touchedPostIds,
    sectorFocus,
    filters,
    filtering,
    sectorsById,
    volunteersById,
  ]);

  const groups = useMemo(() => {
    if (boardView === 'ljudje') {
      const proposedTo = new Set(proposals.map((p) => p.volunteerId));
      // Plan mode narrows this row set the same way it narrows the posts: to
      // the people the plan asks something of.
      const inPlay = data.volunteers.filter((v) =>
        planFocusOnly && proposedTo.size > 0
          ? proposedTo.has(v.id)
          : proposedTo.has(v.id) ||
            data.posts.some((p) => p.shifts.some((s) => s.volunteerId === v.id)),
      );
      return [{ key: 'ljudje', label: 'Prostovoljci', posts: [], people: inPlay }];
    }
    return TYPE_ORDER.filter((t) => visiblePosts.some((p) => p.type === t.key)).map((t) => ({
      key: t.key,
      label: t.label,
      posts: visiblePosts.filter((p) => p.type === t.key),
      people: [] as PoolVolunteer[],
    }));
  }, [boardView, visiblePosts, data, proposals, planFocusOnly]);

  const ticks = hourTicks(axis);
  // The lens reads a post row: "Po ljudeh" already is one person per row.
  const rowLens = boardView === 'mesta' ? lens : null;

  return (
    <div
      className={`tl-board${rowLens ? ' tl-board--lens' : ''}`}
      ref={boardRef}
      // The density floor depends on how many hours this day's axis spans. In
      // rem, like the CSS fallback, so it still tracks the reader's font size.
      style={{ '--tl-track-min': `${minTrackWidth(axis) / 16}rem` } as React.CSSProperties}
    >
      <div className="tl-grid">
        {phase === 'live' ? (
          <div
            className="tl-nowline"
            style={{
              left: `calc(var(--tl-label-w) + (100% - var(--tl-label-w)) * ${minToPct(axis, now) / 100})`,
            }}
          />
        ) : null}
        <div className="tl-axis-label">
          {boardView === 'ljudje' ? 'PROSTOVOLJEC' : 'REDARSKO MESTO'}
        </div>
        <div className="tl-axis">
          {ticks.slice(0, -1).map((min) => (
            <div key={min} className="tl-axis-tick" style={{ left: `${minToPct(axis, min)}%` }}>
              {minutesToHm(min)}
            </div>
          ))}
        </div>

        {groups.map((g) => {
          const collapsed = collapsedGroups[g.key];
          // Counts what the group is actually showing, so a filtered board
          // cannot say "28 opozoril" above a single visible row.
          const warnCount =
            boardView === 'mesta'
              ? g.posts.reduce(
                  (a, p) =>
                    a +
                    (!filtering || gapMatches(filters, p) ? (targetsByPost.get(p.id) ?? 0) : 0) +
                    p.shifts.filter(
                      (s) =>
                        conflictShiftIds.has(s.id) &&
                        (!filtering ||
                          blockMatches(
                            filters,
                            p,
                            s,
                            s.volunteerId ? volunteersById.get(s.volunteerId) : undefined,
                          )),
                    ).length,
                  0,
                )
              : 0;
          return (
            <GroupRows
              key={g.key}
              group={g}
              collapsed={Boolean(collapsed)}
              warnCount={warnCount}
              toggle={() => toggleGroup(g.key)}
              data={data}
              volunteersById={volunteersById}
              sectorsById={sectorsById}
              conflictShiftIds={conflictShiftIds}
              mismatchShiftIds={mismatchShiftIds}
              checkinMap={checkinMap}
              liveViews={liveViews}
              live={live}
              now={now}
              proposals={proposals}
              onAcceptProposal={onAcceptProposal}
              onRejectProposal={onRejectProposal}
              onFocusSector={setSectorFocus}
              filters={filtering ? filters : EMPTY_FILTERS}
              canResize={canResize}
              lens={rowLens}
            />
          );
        })}
      </div>
    </div>
  );
}

function GroupRows(props: {
  group: { key: string; label: string; posts: TimelinePost[]; people: PoolVolunteer[] };
  collapsed: boolean;
  warnCount: number;
  toggle: () => void;
  data: TimelinePayload;
  volunteersById: Map<string, PoolVolunteer>;
  sectorsById: Map<string, { name: string }>;
  conflictShiftIds: Set<string>;
  mismatchShiftIds: Set<string>;
  checkinMap: Map<string, ShiftCheckins>;
  liveViews: Map<string, LiveShiftView>;
  live: boolean;
  now: number;
  proposals: AutofillProposal[];
  onAcceptProposal: (p: AutofillProposal) => void;
  onRejectProposal: (p: AutofillProposal) => void;
  onFocusSector: (name: string | null) => void;
  filters: ReadonlySet<FilterChip>;
  canResize: boolean;
  lens: BoardLens | null;
}) {
  const { group, collapsed, warnCount, toggle } = props;
  const count =
    group.people.length > 0 ? `${group.people.length} oseb` : `${group.posts.length} mest`;
  return (
    <>
      <div className="tl-group-label" onClick={toggle}>
        <span>{collapsed ? '▸' : '▾'}</span>
        {group.label}
        <span className="tl-group-meta">{count}</span>
        {warnCount > 0 ? <span className="tl-group-warn">{warnCount} opozoril</span> : null}
      </div>
      <div className="tl-group-track" />
      {!collapsed &&
        group.posts.map((p) => (
          <PostRow key={p.id} post={p} {...props} />
        ))}
      {!collapsed &&
        group.people.map((v) => (
          <PersonRow key={v.id} person={v} {...props} />
        ))}
    </>
  );
}

function PostRow({
  post,
  data,
  volunteersById,
  sectorsById,
  conflictShiftIds,
  mismatchShiftIds,
  checkinMap,
  liveViews,
  live,
  now,
  proposals,
  onAcceptProposal,
  onRejectProposal,
  onFocusSector,
  filters,
  canResize,
  lens,
}: {
  post: TimelinePost;
  /** Already spread in by GroupRows; the hover card needs the day and the teams. */
  data: TimelinePayload;
  volunteersById: Map<string, PoolVolunteer>;
  sectorsById: Map<string, { name: string }>;
  conflictShiftIds: Set<string>;
  mismatchShiftIds: Set<string>;
  checkinMap: Map<string, ShiftCheckins>;
  liveViews: Map<string, LiveShiftView>;
  live: boolean;
  now: number;
  proposals: AutofillProposal[];
  onAcceptProposal: (p: AutofillProposal) => void;
  onRejectProposal: (p: AutofillProposal) => void;
  onFocusSector: (name: string | null) => void;
  /** Empty means no chip is on and everything renders. */
  filters: ReadonlySet<FilterChip>;
  canResize: boolean;
  lens: BoardLens | null;
}) {
  const axis = useAxis();
  const { selectGap, selectedGap, selectedShiftId, gapPiece, assignPreview } = useUi();
  const cov = rowCoverage(post);
  const gappy = post.gaps.length > 0 || post.shifts.some((s) => s.state === 'open');
  const rowProposals = proposals.filter((p) => p.gap.postId === post.id);
  // An open slot a proposal is staffing is drawn as the proposal, not twice:
  // its range is inside the row's gaps, so the two would sit on top of each other.
  const staffed = new Set(
    rowProposals.map((p) => p.gap.targetShiftId).filter((id): id is string => id !== null),
  );

  // What the chips leave standing. The selected block always survives: the
  // detail panel and every Opozorila item address a shift by id, so filtering
  // one off screen would leave the panel describing something invisible.
  const shown = (s: Shift) =>
    filters.size === 0 ||
    s.id === selectedShiftId ||
    blockMatches(filters, post, s, s.volunteerId ? volunteersById.get(s.volunteerId) : undefined);
  const shownGap = (g: { fromMin: number; toMin: number }) =>
    filters.size === 0 ||
    gapMatches(filters, post) ||
    (selectedGap?.postId === post.id &&
      selectedGap.fromMin === g.fromMin &&
      selectedGap.toMin === g.toMin);

  // The row is the one drop target; what a drop at the pointer does is `dropPlan`.
  const { setNodeRef, isOver } = useDroppable({
    id: `track:${post.id}`,
    data: { type: 'track', postId: post.id },
  });
  const drop = useDropPreview((st) => (st.preview?.postId === post.id ? st.preview : null));

  return (
    <>
      <div
        className={`tl-row-label${gappy ? ' tl-row-label--gappy' : ''}`}
        data-post-row={post.id}
        onClick={() => onFocusSector(sectorsById.get(post.sectorId)?.name ?? null)}
        title={`${post.code} · ${post.micro} — klikni za fokus na sektor`}
      >
        <span className={`tl-row-rail ${RAIL_CLASS[post.type] ?? ''}`} />
        <span className="tl-row-main">
          <span className="tl-row-code">
            {post.code}
            {post.urgent ? <span className="tl-urgent">NUJEN</span> : null}
          </span>
          <span className="tl-row-micro">{post.micro}</span>
        </span>
        {cov !== null ? (
          <span
            className={`tl-row-cov ${cov === 100 ? 'tl-cov--full' : cov >= 70 ? 'tl-cov--mid' : 'tl-cov--low'}`}
          >
            {cov}%
          </span>
        ) : null}
      </div>
      <div
        ref={setNodeRef}
        data-track-post={post.id}
        className={`tl-track${gappy ? ' tl-track--gappy' : ''}`}
        style={{ outline: isOver ? '2px solid oklch(0.6 0.1 245)' : undefined }}
      >
        {hourTicks(axis).slice(1, -1).map((min) => (
          <div key={min} className="tl-hourline" style={{ left: `${minToPct(axis, min)}%` }} />
        ))}
        <ClosedBands windows={post.windows} />
        {lens
          ? lens.bands
              .filter((b) => b.value === 'ni-na-voljo' || b.value === 'neznano')
              .map((b) => (
                <div
                  key={`lens-${b.fromMin}`}
                  className={b.value === 'ni-na-voljo' ? 'tl-lens-closed' : 'tl-lens-unknown'}
                  aria-hidden="true"
                  style={{ left: `${minToPct(axis, b.fromMin)}%`, width: `${spanPct(axis, b.fromMin, b.toMin)}%` }}
                />
              ))
          : null}
        {post.gaps.filter(shownGap).map((g) => (
          <GapSegment
            key={`${g.fromMin}-${g.toMin}`}
            gap={g}
            selected={
              selectedGap?.postId === post.id &&
              selectedGap.fromMin === g.fromMin &&
              selectedGap.toMin === g.toMin
            }
            covered={rowProposals.some(
              (p) => p.gap.fromMin === g.fromMin && p.gap.toMin === g.toMin,
            )}
            onClick={() => selectGap({ postId: post.id, fromMin: g.fromMin, toMin: g.toMin })}
          />
        ))}
        {rowProposals.map((p) => (
          <ProposalBlock
            key={`${p.gap.fromMin}-${p.volunteerId}`}
            proposal={p}
            label={p.volunteerName}
            onAccept={onAcceptProposal}
            onReject={onRejectProposal}
          />
        ))}
        {/* Lanes are assigned over the whole row and only then narrowed, so a
            surviving block keeps its height and position when a chip is
            toggled rather than jumping as the row collapses to one lane. */}
        {assignLanes(post.shifts.filter((s) => !staffed.has(s.id)))
          .filter(({ item }) => shown(item))
          .map(({ item: shift, lane, lanes }) => (
          <ShiftBlock
            key={shift.id}
            shift={shift}
            data={data}
            lane={lane}
            lanes={lanes}
            label={
              shift.volunteerId
                ? (() => {
                    const holder = volunteersById.get(shift.volunteerId);
                    return holder ? holderLabel(holder) : '?';
                  })()
                : 'PROSTO'
            }
            volunteer={shift.volunteerId ? volunteersById.get(shift.volunteerId) : undefined}
            conflict={conflictShiftIds.has(shift.id)}
            mismatch={mismatchShiftIds.has(shift.id)}
            checkinMap={checkinMap}
            liveViews={liveViews}
            live={live}
            now={now}
            resizeIn={canResize && shift.state !== 'done' ? post : undefined}
            lensId={lens?.volunteerId}
          />
        ))}
        {lens
          ? (lens.fitsByPost.get(post.id) ?? []).map((f) => (
              <LensFitBlock key={f.target.kind === 'open' ? f.target.shiftId : `gap-${f.target.fromMin}`} fit={f} lens={lens} />
            ))
          : null}
        {/* What the side panel is about to do, drawn before it happens. */}
        {gapPiece?.postId === post.id ? (
          <div
            className="tl-piece"
            aria-hidden="true"
            style={{
              left: `${minToPct(axis, gapPiece.fromMin)}%`,
              width: `${spanPct(axis, gapPiece.fromMin, gapPiece.toMin)}%`,
            }}
          />
        ) : null}
        {assignPreview?.postId === post.id ? (
          <div
            className={`tl-ghost tl-ghost--${assignPreview.tone}`}
            aria-hidden="true"
            style={{
              left: `${minToPct(axis, assignPreview.fromMin)}%`,
              width: `${spanPct(axis, assignPreview.fromMin, assignPreview.toMin)}%`,
            }}
          >
            <span className="tl-ghost-name">{assignPreview.name}</span>
            <span className="tl-ghost-time">{range(assignPreview.fromMin, assignPreview.toMin)}</span>
          </div>
        ) : null}
        {drop ? (
          <div
            className={`tl-ghost tl-ghost--${drop.tone}`}
            aria-hidden="true"
            style={{
              left: `${minToPct(axis, drop.fromMin)}%`,
              width: `${spanPct(axis, drop.fromMin, drop.toMin)}%`,
            }}
          >
            <span className="tl-ghost-name">{drop.tone === 'replace' ? drop.label : drop.name}</span>
            <span className="tl-ghost-time">{range(drop.fromMin, drop.toMin)}</span>
          </div>
        ) : null}
      </div>
    </>
  );
}

function PersonRow({
  person,
  data,
  conflictShiftIds,
  mismatchShiftIds,
  checkinMap,
  liveViews,
  live,
  now,
  proposals,
  onAcceptProposal,
  onRejectProposal,
}: {
  person: PoolVolunteer;
  data: TimelinePayload;
  conflictShiftIds: Set<string>;
  mismatchShiftIds: Set<string>;
  checkinMap: Map<string, ShiftCheckins>;
  liveViews: Map<string, LiveShiftView>;
  live: boolean;
  now: number;
  proposals: AutofillProposal[];
  onAcceptProposal: (p: AutofillProposal) => void;
  onRejectProposal: (p: AutofillProposal) => void;
}) {
  const axis = useAxis();
  const shifts = data.posts.flatMap((p) =>
    p.shifts.filter((s) => s.volunteerId === person.id).map((s) => ({ shift: s, post: p })),
  );
  const mine = proposals.filter((p) => p.volunteerId === person.id);
  if (shifts.length === 0 && mine.length === 0) return null;
  const clash = shifts.some(({ shift }) => conflictShiftIds.has(shift.id));
  const proposedMin = mine.reduce((a, p) => a + (p.gap.toMin - p.gap.fromMin), 0);
  const postOf = (id: string) => data.posts.find((p) => p.id === id);

  // Shifts and proposals share the lanes: what the plan asks of this person
  // reads as the length of their row.
  const items = [
    ...shifts.map(({ shift, post }) => ({
      kind: 'shift' as const,
      fromMin: shift.fromMin,
      toMin: shift.toMin,
      shift,
      post,
    })),
    ...mine.map((p) => ({
      kind: 'proposal' as const,
      fromMin: p.gap.fromMin,
      toMin: p.gap.toMin,
      proposal: p,
    })),
  ];

  // What this person can actually see is older than what this row shows
  // (SPEC §1.1F). Asked through the shared predicate, so the row, the roster
  // badge and the Pregled count cannot answer it three different ways — and
  // the label comes from the state rather than being re-derived here.
  const publication = publicationState({
    scheduleVersion: person.scheduleVersion,
    briefingSentVersion: person.briefingSentVersion ?? null,
    // The row exists because they work today; a proposal is not a shift yet.
    hasShifts: shifts.some(({ shift }) => publishable(shift)),
  });
  // A team's place is never briefed, so it can never be behind one (SPEC
  // §1.1J) — without this every PGD and GRS row carries a permanent amber
  // tint and a "brez objave" chip that no action can clear.
  // Nor somebody not coming: nothing is published to them any more (SPEC §1.1K).
  // And nobody for a login that is not told what was published (SPEC §1.1L).
  const unpublished =
    person.briefingSentVersion !== undefined &&
    !isExternal(person) &&
    !isCancelled(person) &&
    isUnpublished(publication);

  return (
    <>
      <div className={`tl-row-label${unpublished ? ' tl-row-label--unpublished' : ''}`}>
        <span className="tl-row-rail" style={{ background: 'oklch(0.85 0.01 95)' }} />
        <span className="tl-row-main">
          <span className="tl-row-code">
            {holderLabel(person)}
            {clash ? <span className="tl-mark-conflict">2×</span> : null}
          </span>
          <span className="tl-row-micro">
            {mine.length > 0
              ? `${plS(shifts.length)} · +${plPred(mine.length)} · skupaj ${formatDuration(
                  person.dayMinutes + proposedMin,
                )}`
              : `${plS(shifts.length)} · ${formatDuration(person.dayMinutes)}`}
            {unpublished ? <span className="tl-mark-unpublished">{publication}</span> : null}
          </span>
        </span>
      </div>
      <div className={`tl-track${unpublished ? ' tl-track--unpublished' : ''}`}>
        {hourTicks(axis).slice(1, -1).map((min) => (
          <div key={min} className="tl-hourline" style={{ left: `${minToPct(axis, min)}%` }} />
        ))}
        {assignLanes(items).map(({ item, lane, lanes }) =>
          item.kind === 'shift' ? (
            <ShiftBlock
              key={item.shift.id}
              shift={item.shift}
              data={data}
              lane={lane}
              lanes={lanes}
              label={`${item.post.code} · ${item.post.micro}`}
              volunteer={person}
              conflict={conflictShiftIds.has(item.shift.id)}
              mismatch={mismatchShiftIds.has(item.shift.id)}
              checkinMap={checkinMap}
              liveViews={liveViews}
              live={live}
              now={now}
            />
          ) : (
            <ProposalBlock
              key={gapKey(item.proposal.gap)}
              proposal={item.proposal}
              label={`${postOf(item.proposal.gap.postId)?.code ?? item.proposal.gap.postCode} · ${
                postOf(item.proposal.gap.postId)?.micro ?? ''
              }`}
              lane={lane}
              lanes={lanes}
              onAccept={onAcceptProposal}
              onReject={onRejectProposal}
            />
          ),
        )}
      </div>
    </>
  );
}

/**
 * One proposed piece on the track. Dashed while it is only a proposal; the
 * ✓/✕ are buttons so the keyboard and a screen reader reach them, and the
 * block carries its gap key so the panel and the keyboard walk can find it.
 */
function ProposalBlock({
  proposal: p,
  label,
  lane,
  lanes,
  onAccept,
  onReject,
}: {
  proposal: AutofillProposal;
  label: string;
  lane?: number;
  lanes?: number;
  onAccept: (p: AutofillProposal) => void;
  onReject: (p: AutofillProposal) => void;
}) {
  const axis = useAxis();
  const { focusedProposal, focusProposal } = useUi();
  const key = gapKey(p.gap);
  const laid =
    lane === undefined || lanes === undefined
      ? null
      : (() => {
          const height = lanes > 1 ? 38 / lanes - 2 : 38;
          return { top: 3 + lane * (height + 2), height, bottom: 'auto' as const };
        })();

  return (
    <div
      data-proposal={key}
      className={[
        'tl-predlog',
        bendsARule(p) ? 'tl-predlog--warn' : '',
        focusedProposal === key ? 'tl-predlog--focused' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        left: `${minToPct(axis, p.gap.fromMin)}%`,
        width: `${spanPct(axis, p.gap.fromMin, p.gap.toMin)}%`,
        ...laid,
      }}
      title={`Predlog: ${p.volunteerName} · ${range(p.gap.fromMin, p.gap.toMin)} — ${proposalReason(p)}`}
      onClick={() => focusProposal(key)}
    >
      <span className="tl-predlog-tag">
        {`PREDLOG${bendsARule(p) ? ' ⚠' : ''}${
          p.warnings.some((w) => w.kind === 'inexperienced') ? ' ⚑' : ''
        }`}
      </span>
      <span className="tl-block-name">{label}</span>
      <span className="tl-predlog-acts">
        <button
          type="button"
          className="tl-predlog-act tl-predlog-act--yes"
          aria-label={`Sprejmi predlog: ${p.volunteerName}, ${p.gap.postCode}, ${range(p.gap.fromMin, p.gap.toMin)}`}
          onClick={(e) => {
            e.stopPropagation();
            onAccept(p);
          }}
        >
          ✓
        </button>
        <button
          type="button"
          className="tl-predlog-act"
          aria-label={`Zavrni predlog: ${p.volunteerName}, ${p.gap.postCode}, ${range(p.gap.fromMin, p.gap.toMin)}`}
          onClick={(e) => {
            e.stopPropagation();
            onReject(p);
          }}
        >
          ✕
        </button>
      </span>
    </div>
  );
}

const BLOCK_CLASS: Record<BlockStatus, string> = {
  potrjen: 'tl-block--potrjen',
  caka: 'tl-block--caka',
  zavrnjen: 'tl-block--zavrnjen',
  opravljeno: 'tl-block--opravljeno',
  prosto: 'tl-block--prosto',
  predlog: 'tl-block--caka',
  zunanja: 'tl-block--zunanja',
};

function ShiftBlock({
  shift,
  data,
  lane,
  lanes,
  label,
  volunteer,
  conflict,
  mismatch,
  checkinMap,
  liveViews,
  live,
  now,
  resizeIn,
  lensId,
}: {
  shift: Shift;
  data: TimelinePayload;
  lane: number;
  lanes: number;
  label: string;
  volunteer: PoolVolunteer | undefined;
  conflict: boolean;
  /** Their sheet closed a slot this shift runs into — planning mode only. */
  mismatch: boolean;
  checkinMap: Map<string, ShiftCheckins>;
  liveViews: Map<string, LiveShiftView>;
  live: boolean;
  now: number;
  /** The post this block sits on, when its edges may be dragged. */
  resizeIn?: TimelinePost;
  /** The person a post row is looked at through: theirs stand out, the rest recede. */
  lensId?: string;
}) {
  const axis = useAxis();
  const [preview, setPreview] = useState<{ fromMin: number; toMin: number } | null>(null);
  const { selectedShiftId, selectShift, selectGap, hoveredVolunteerId, setHoveredVolunteer } =
    useUi();
  const status = blockStatus(shift, volunteer);
  const view = liveViews.get(shift.id);
  const ciSt = ciState(shift, checkinMap, view);
  const lateLv = lateLevel(shift, checkinMap, now, live, volunteer?.externalGroupId != null, view);
  const height = lanes > 1 ? 38 / lanes - 2 : 38;
  const top = 3 + lane * (height + 2);
  const dimmed =
    lensId !== undefined
      ? shift.volunteerId !== lensId
      : hoveredVolunteerId !== null && shift.volunteerId !== null && shift.volunteerId !== hoveredVolunteerId;
  const hovered =
    lensId !== undefined ? shift.volunteerId === lensId : hoveredVolunteerId !== null && shift.volunteerId === hoveredVolunteerId;
  const stale = volunteer && shift.state !== 'done' ? briefingStale(volunteer) : false;

  // live check-in state overrides the confirmation colouring
  const liveClass =
    lateLv >= 0
      ? ''
      : live && ciSt === 'opravljeno'
        ? 'tl-block--opravljeno'
        : live && ciSt === 'prijavljen'
          ? 'tl-block--namestu'
          : '';
  const lateStyle = lateLv === -1 ? null : LATE_BLOCK[lateLv];
  // What the block draws: the drag's preview while one is live, else the truth.
  const at = preview ?? shift;

  const mark =
    lateLv >= 0 ? (
      <span title={lateLv === 0 ? 'brez prijave' : 'zamuja'}>!</span>
    ) : shift.state === 'done' || (live && ciSt === 'opravljeno') ? (
      <span title="izmena opravljena — zaklenjena">✓</span>
    ) : live && ciSt === 'prijavljen' ? (
      <span title="prijavljen na mestu">●</span>
    ) : stale ? (
      <span className="tl-mark-brief" title="brifing ni poslan">
        ✉
      </span>
    ) : null;

  const block = (
    <div
      className={[
        'tl-block',
        liveClass || BLOCK_CLASS[status],
        conflict && status !== 'opravljeno' ? 'tl-block--conflict' : '',
        selectedShiftId === shift.id ? 'tl-block--selected' : '',
        hovered ? 'tl-block--hovered' : '',
        dimmed ? 'tl-block--dimmed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        left: `${minToPct(axis, at.fromMin)}%`,
        width: `${spanPct(axis, at.fromMin, at.toMin)}%`,
        top,
        height,
        ...(lateStyle
          ? { background: lateStyle.bg, borderColor: lateStyle.bd, color: lateStyle.fg }
          : null),
      }}
      // A staffed block hands this job to the hover card, which says all of it
      // and more — two tooltips over one block is one too many.
      title={volunteer ? undefined : `${label} · ${range(at.fromMin, at.toMin)}`}
      onClick={() => {
        if (status === 'prosto') {
          selectGap({
            postId: shift.postId,
            fromMin: shift.fromMin,
            toMin: shift.toMin,
            shiftId: shift.id,
          });
        } else {
          selectShift(shift.id);
        }
      }}
      onMouseEnter={() => shift.volunteerId && setHoveredVolunteer(shift.volunteerId)}
      onMouseLeave={() => setHoveredVolunteer(null)}
    >
      <span className="tl-block-dot" style={lateStyle ? { background: lateStyle.dot } : undefined} />
      <span className="tl-block-name">{label}</span>
      <span className="tl-block-time">{range(at.fromMin, at.toMin)}</span>
      <span className="tl-block-mark">
        {mark}
        {mismatch ? (
          <span className="tl-mark-avail" title="javljeno, da takrat ni na voljo">
            ▲
          </span>
        ) : null}
        {conflict ? (
          <span className="tl-mark-conflict" title="dvojna zasedba">
            2×
          </span>
        ) : null}
      </span>
      {resizeIn ? (
        <ShiftHandles
          shift={shift}
          post={resizeIn}
          volunteer={volunteer}
          live={live}
          widthPct={spanPct(axis, shift.fromMin, shift.toMin)}
          onPreview={setPreview}
        />
      ) : null}
    </div>
  );

  // Nobody to describe on an open slot, and a resize in progress wants the
  // board to itself.
  if (!volunteer) return block;
  return (
    <VolunteerHoverCard volunteer={volunteer} shift={shift} data={data} disabled={preview !== null}>
      {block}
    </VolunteerHoverCard>
  );
}

/**
 * The two draggable edges of an izmena.
 *
 * Raw pointer events with capture rather than dnd-kit: the pool's drag-and-drop
 * is a different gesture on the same surface, and a resize must not start one.
 * The click that follows a release is stopped here too — it would otherwise
 * bubble to the block and move the side panel out from under the coordinator.
 */
function ShiftHandles({
  shift,
  post,
  volunteer,
  live,
  widthPct,
  onPreview,
}: {
  shift: Shift;
  post: TimelinePost;
  volunteer: PoolVolunteer | undefined;
  live: boolean;
  widthPct: number;
  onPreview: (r: { fromMin: number; toMin: number } | null) => void;
}) {
  const axis = useAxis();
  const dayKey = useDayKey();
  const { silentOverride, setUndo, setPendingResize } = useUi();
  const { update } = useShiftMutations();
  const drag = useRef<{
    edge: 'from' | 'to';
    track: HTMLElement;
    bounds: ResizeBounds;
    next: { fromMin: number; toMin: number };
  } | null>(null);

  // Two handles and a click target do not fit in a block this narrow.
  if (widthPct < MIN_HANDLE_PCT) return null;

  const end = () => {
    onPreview(null);
    drag.current = null;
  };

  const start = (edge: 'from' | 'to') => (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const track = e.currentTarget.closest('.tl-track');
    if (!(track instanceof HTMLElement)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      edge,
      track,
      bounds: resizeBounds(
        shift,
        post.windows,
        post.shifts.filter((o) => o.id !== shift.id),
        axis,
      ),
      next: { fromMin: shift.fromMin, toMin: shift.toMin },
    };
  };

  const move = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    // Re-measured every move: the board scrolls, so a rect cached at
    // pointerdown drifts under the pointer.
    const rect = d.track.getBoundingClientRect();
    const min = snap15(xToMin(axis, e.clientX - rect.left, rect.width));
    const b = d.bounds;
    d.next =
      d.edge === 'from'
        ? { fromMin: clampMin(min, b.fromFloor, b.fromCeil), toMin: shift.toMin }
        : { fromMin: shift.fromMin, toMin: clampMin(min, b.toFloor, b.toCeil) };
    onPreview(d.next);
  };

  const finish = (e: React.PointerEvent) => {
    const d = drag.current;
    end();
    if (!d) return;
    const { fromMin, toMin } = d.next;
    if (fromMin === shift.fromMin && toMin === shift.toMin) return;
    // The board may have moved under the drag (SSE refetch, another
    // coordinator): commit only against the row this drag started on.
    if (e.currentTarget instanceof HTMLElement && !e.currentTarget.isConnected) return;

    // Changing the times is material, so it sends the volunteer back to čaka
    // and re-marks the briefing — and no undo can give those back. When the
    // drag would cost somebody that, it asks first instead of committing.
    const silent = silentOverride ?? live;
    // Somebody not coming is asked nothing: no version moves for them, and
    // shortening a shift they kept is what the Ne pride dialog asks for.
    const asked = volunteer && !isCancelled(volunteer) ? volunteer : undefined;
    const costs =
      !silent && asked && !isExternal(asked)
        ? shiftConfirmation(shift, asked) === 'potrjen'
          ? ('potrjen' as const)
          : briefingStale(asked)
            ? null
            : ('brifing' as const)
        : null;
    // The same question every assignment gesture asks: does the new range run
    // into a slot this person marked "ni na voljo"? Only the hours the drag
    // adds count — a slot the shift already overlapped was settled when the
    // shift was made, and asking about it again on every nudge would be noise.
    const wasClosed = asked ? gapMismatch(asked, shift, dayKey) : [];
    const closed = asked
      ? gapMismatch(asked, { fromMin, toMin }, dayKey).filter(
          (s) => !wasClosed.some((w) => w.id === s.id),
        )
      : [];
    if ((costs || closed.length > 0) && volunteer) {
      setPendingResize({
        shiftId: shift.id,
        updatedAt: shift.updatedAt,
        postCode: post.code,
        name: volunteer.name,
        fromMin,
        toMin,
        wasFromMin: shift.fromMin,
        wasToMin: shift.toMin,
        reason: costs,
        closed,
      });
      return;
    }
    update.mutate(
      {
        id: shift.id,
        updatedAt: shift.updatedAt,
        fromMin,
        toMin,
        silent,
      },
      {
        onSuccess: (updated: { updatedAt: string }) =>
          setUndo({
            head: `${post.code} — izmena spremenjena`,
            sub: range(fromMin, toMin),
            run: () =>
              update.mutate({
                id: shift.id,
                updatedAt: updated.updatedAt,
                fromMin: shift.fromMin,
                toMin: shift.toMin,
                silent: true,
              }),
          }),
      },
    );
  };

  const handle = (edge: 'from' | 'to') => (
    <span
      key={edge}
      className={`tl-block-handle tl-block-handle--${edge}`}
      title={edge === 'from' ? 'povleci za spremembo začetka' : 'povleci za spremembo konca'}
      onPointerDown={start(edge)}
      onPointerMove={move}
      onPointerUp={finish}
      onPointerCancel={end}
      onLostPointerCapture={end}
      onClick={(e) => e.stopPropagation()}
    />
  );

  return (
    <>
      {handle('from')}
      {handle('to')}
    </>
  );
}

function clampMin(min: number, floor: number, ceil: number): number {
  return Math.min(ceil, Math.max(floor, min));
}

/**
 * Where the lens person fits on this row: green, amber with a warning, blue
 * where nobody else is recommended. A click places them on exactly that piece.
 */
function LensFitBlock({ fit, lens }: { fit: LensFit; lens: BoardLens }) {
  const axis = useAxis();
  const widthPct = spanPct(axis, fit.piece.fromMin, fit.piece.toMin);
  const time = range(fit.piece.fromMin, fit.piece.toMin);
  const label = widthPct > longLabelPct(axis) ? `+ ${time}${fit.only ? ' · edini' : ''}` : '+';
  const className = `tl-fit${fit.only ? ' tl-fit--only' : fit.candidate.group === 'warn' ? ' tl-fit--warn' : ''}`;
  const style = { left: `${minToPct(axis, fit.piece.fromMin)}%`, width: `${widthPct}%` };
  const title = `${lens.name} → ${fit.post.code} ${time}${fit.only ? ' · edini priporočeni' : ''}`;
  if (!lens.canAct) {
    return (
      <div className={`${className} tl-fit--static`} style={style} title={title}>
        {label}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      style={style}
      title={`Dodeli: ${title}`}
      onClick={(e) => {
        e.stopPropagation();
        lens.onAssign(fit);
      }}
    >
      {label}
    </button>
  );
}

function GapSegment({
  gap,
  selected,
  covered,
  onClick,
}: {
  gap: { fromMin: number; toMin: number };
  selected: boolean;
  covered: boolean;
  onClick: () => void;
}) {
  const axis = useAxis();
  if (covered) return null;
  const widthPct = spanPct(axis, gap.fromMin, gap.toMin);
  return (
    <div
      className={`tl-gap${selected ? ' tl-gap--selected' : ''}`}
      style={{ left: `${minToPct(axis, gap.fromMin)}%`, width: `${widthPct}%` }}
      title={`Nepokrito ${range(gap.fromMin, gap.toMin)} — klikni za kandidate`}
      onClick={onClick}
    >
      {widthPct > longLabelPct(axis) ? `PROSTO ${range(gap.fromMin, gap.toMin)}` : 'PROSTO'}
    </div>
  );
}

function ClosedBands({ windows }: { windows: { fromMin: number; toMin: number }[] }) {
  const axis = useAxis();
  if (windows.length === 0) return null;
  const sorted = [...windows].sort((a, b) => a.fromMin - b.fromMin);
  const bands: { fromMin: number; toMin: number; edge: boolean }[] = [];
  // The edge bands are drawn to the axis, so they shrink to nothing on a day
  // whose axis starts at its first window — which is most days.
  if (sorted[0]!.fromMin > axis.fromMin) {
    bands.push({ fromMin: axis.fromMin, toMin: sorted[0]!.fromMin, edge: true });
  }
  for (let i = 0; i < sorted.length - 1; i++) {
    bands.push({ fromMin: sorted[i]!.toMin, toMin: sorted[i + 1]!.fromMin, edge: false });
  }
  const last = sorted.at(-1)!;
  if (last.toMin < axis.toMin) bands.push({ fromMin: last.toMin, toMin: axis.toMin, edge: true });
  return (
    <>
      {bands.map((b) => (
        <div
          key={`${b.fromMin}-${b.toMin}`}
          className={`tl-closed ${b.edge ? 'tl-closed--edge' : 'tl-closed--inner'}`}
          style={{ left: `${minToPct(axis, b.fromMin)}%`, width: `${spanPct(axis, b.fromMin, b.toMin)}%` }}
          title={
            b.edge ? 'izven odprtja mesta' : `mesto zaprto ${range(b.fromMin, b.toMin)}`
          }
        >
          {!b.edge && b.toMin - b.fromMin >= 120 ? 'ZAPRTO' : ''}
        </div>
      ))}
    </>
  );
}

/**
 * The gap chip shows its time range only when the block is wide enough. The
 * threshold is the share of the track that 76px occupies at the narrowest
 * layout, so the long label never overflows — on wider boards there is simply
 * more slack. It depends on the axis: a shorter day packs the same hours into
 * the same minimum width, so a block of the same duration is a larger share.
 */
function longLabelPct(axis: Axis): number {
  return (76 / minTrackWidth(axis)) * 100;
}

/** Stack anything with a time range into non-overlapping lanes. */
function assignLanes<T extends { fromMin: number; toMin: number }>(
  items: T[],
): { item: T; lane: number; lanes: number }[] {
  const sorted = [...items].sort((a, b) => a.fromMin - b.fromMin || a.toMin - b.toMin);
  const laneEnds: number[] = [];
  const placed = sorted.map((item) => {
    let lane = laneEnds.findIndex((end) => end <= item.fromMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.toMin);
    } else {
      laneEnds[lane] = item.toMin;
    }
    return { item, lane };
  });
  const lanes = Math.max(1, laneEnds.length);
  return placed.map((p) => ({ ...p, lanes }));
}
